/**
 * Memorial Moments Carousel — Bulletproof click-to-animate.
 * Direct R2 CDN URLs for instant media loading. No proxy overhead.
 * Photo + overlay → click → crossfade to video → click → reset.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MomentSlide = ({ moment, isActive }) => {
  const vidRef = useRef(null);
  const playingRef = useRef(false);
  const [playing, setPlaying] = useState(false);

  const photo = moment.photoUrl;
  const video = moment.videoUrl;

  // Load video src only when active, cleanup when not
  useEffect(() => {
    const v = vidRef.current;
    if (!v || !video) return;

    if (isActive) {
      if (!v.src || v.src !== video) {
        v.src = video;
      }
    }

    return () => {
      if (!isActive) {
        v.pause();
        v.currentTime = 0;
        v.removeAttribute('src');
        v.load();
        playingRef.current = false;
        setPlaying(false);
      }
    };
  }, [isActive, video]);

  const handleClick = useCallback(() => {
    if (!video) return;
    const v = vidRef.current;
    if (!v) return;

    if (playingRef.current) {
      v.pause();
      v.currentTime = 0;
      playingRef.current = false;
      setPlaying(false);
    } else {
      v.currentTime = 0;
      v.play()
        .then(() => { playingRef.current = true; setPlaying(true); })
        .catch(() => {});
    }
  }, [video]);

  if (!photo && !video) return null;

  return (
    <div
      onClick={handleClick}
      className={`relative w-full max-w-2xl mx-auto select-none ${video ? 'cursor-pointer' : ''}`}
      data-testid={`moment-slide-${moment.slot}`}
    >
      {/* Glow — reduced intensity on mobile, warm cinematic feel */}
      <div className="absolute -inset-3 sm:-inset-4 md:-inset-6 lg:-inset-8 bg-gradient-to-r from-purple-500/15 via-purple-400/22 to-purple-500/15 sm:from-purple-500/18 sm:via-purple-400/25 sm:to-purple-500/18 rounded-3xl blur-2xl lg:blur-3xl opacity-50 sm:opacity-55" />
      <div className="relative rounded-2xl overflow-hidden bg-black shadow-2xl shadow-purple-500/15 ring-1 ring-purple-500/15 sm:shadow-purple-500/20 sm:ring-purple-500/20">
        {/* Photo */}
        {photo && (
          <img
            src={photo}
            alt="Memorial moment"
            draggable="false"
            className="w-full h-auto block"
            style={{
              opacity: playing ? 0 : 1,
              transition: 'opacity 0.5s ease-in-out',
              willChange: 'opacity',
              maxHeight: '80vh',
              objectFit: 'contain',
            }}
          />
        )}

        {/* Video */}
        {video && (
          <video
            ref={vidRef}
            className="absolute inset-0 w-full h-full object-contain"
            style={{
              opacity: playing ? 1 : 0,
              transition: 'opacity 0.5s ease-in-out',
              willChange: 'opacity',
            }}
            muted
            loop
            playsInline
            preload="none"
          />
        )}

        {/* Overlay — uses CSS opacity to avoid mount/unmount */}
        {video && (
          <div
            className="absolute inset-0 z-10 flex flex-col items-center justify-center"
            style={{
              background: 'rgba(0,0,0,0.18)',
              opacity: playing ? 0 : 1,
              pointerEvents: playing ? 'none' : 'auto',
              transition: 'opacity 0.3s ease-out',
            }}
            data-testid="tap-overlay"
          >
            <div
              className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center mb-3"
              style={{ background: 'rgba(234,179,8,0.15)', border: '1.5px solid rgba(234,179,8,0.4)', minWidth: 48, minHeight: 48 }}
            >
              <svg className="w-8 h-8 md:w-10 md:h-10 ml-1" viewBox="0 0 24 24" fill="rgba(234,179,8,0.9)">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
            <p
              className="text-yellow-100/90 text-sm md:text-base font-medium tracking-wide"
              style={{ textShadow: '0 2px 10px rgba(0,0,0,0.9)' }}
            >
              Tap to bring this memory to life
            </p>
          </div>
        )}

        {/* Photo only */}
        {!video && photo && (
          <div className="absolute inset-0 z-10 flex items-end justify-center pb-6">
            <p className="text-white/70 text-sm bg-black/50 px-4 py-2 rounded-full backdrop-blur-sm">
              Animation coming soon
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const MemorialMomentsCarousel = ({ isAdmin = false }) => {
  const [moments, setMoments] = useState([]);
  const [settings, setSettings] = useState({});
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        setLoading(true);
        const [s, m] = await Promise.all([
          axios.get(`${API}/memorial-settings`),
          axios.get(`${API}/memorial-moments`),
        ]);
        if (!cancel) { setSettings(s.data || {}); setMoments(m.data || []); }
      } catch (e) { if (!cancel) setError(e.message); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, []);

  const valid = moments.filter((m) => m.photoUrl || m.videoUrl);
  const on = settings.enableCarousel !== false && settings.enableShowcase !== false;

  const go = useCallback(
    (d) => setIdx((p) => {
      const l = valid.length;
      return l === 0 ? 0 : d < 0 ? (p === 0 ? l - 1 : p - 1) : p >= l - 1 ? 0 : p + 1;
    }),
    [valid.length],
  );

  if (loading)
    return <div className="py-8"><div className="max-w-2xl mx-auto aspect-[4/3] rounded-2xl bg-zinc-900/30 animate-pulse" /></div>;

  if (!(on && valid.length > 0 && !error)) {
    if (isAdmin && on && valid.length === 0 && !error)
      return (
        <section className="py-4 px-4" data-testid="memorial-moments-section">
          <div className="max-w-5xl mx-auto">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4" data-testid="moments-admin-diagnostic">
              <AlertCircle className="w-5 h-5 text-amber-400 inline mr-2" />
              <span className="text-amber-300/80 text-xs">Upload at least one photo in Admin &gt; Memorial &gt; Moments.</span>
            </div>
          </div>
        </section>
      );
    return null;
  }

  return (
    <section className="py-8 px-3 sm:px-4" data-testid="memorial-moments-section">
      <div className="max-w-5xl lg:max-w-6xl xl:max-w-7xl mx-auto">
        <div className="relative" data-testid="memorial-moments-carousel">
          <div className="text-center mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">Bring Memories to Life</h2>
            <p className="text-zinc-400 text-sm md:text-base">Tap each photo to watch it come alive</p>
          </div>

          <div className="relative flex items-center justify-center">
            <button onClick={() => go(-1)} aria-label="Previous moment"
              className="absolute left-0 md:left-2 z-30 w-11 h-11 md:w-14 md:h-14 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95 group"
              style={{ background: 'rgba(234,179,8,0.12)', border: '2px solid rgba(234,179,8,0.45)', boxShadow: '0 4px 12px rgba(234,179,8,0.15)' }}
              data-testid="moments-prev-btn">
              <ChevronLeft className="w-6 h-6 md:w-7 md:h-7 text-yellow-400 group-hover:text-yellow-300" />
            </button>

            <div className="w-full max-w-2xl lg:max-w-4xl xl:max-w-5xl mx-8 sm:mx-14 md:mx-20">
              {valid.map((m, i) => (
                <div key={m.slot || i} style={{ display: i === idx ? 'block' : 'none' }}>
                  <MomentSlide moment={m} isActive={i === idx} />
                </div>
              ))}
            </div>

            <button onClick={() => go(1)} aria-label="Next moment"
              className="absolute right-0 md:right-2 z-30 w-11 h-11 md:w-14 md:h-14 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95 group"
              style={{ background: 'rgba(234,179,8,0.12)', border: '2px solid rgba(234,179,8,0.45)', boxShadow: '0 4px 12px rgba(234,179,8,0.15)' }}
              data-testid="moments-next-btn">
              <ChevronRight className="w-6 h-6 md:w-7 md:h-7 text-yellow-400 group-hover:text-yellow-300" />
            </button>
          </div>

          {valid.length > 1 && (
            <div className="flex justify-center gap-2.5 mt-6" data-testid="moments-dots">
              {valid.map((_, i) => (
                <button key={i} onClick={() => setIdx(i)} aria-label={`Moment ${i + 1}`}
                  className="rounded-full transition-all duration-300"
                  style={{ height: 10, width: i === idx ? 28 : 10, background: i === idx ? 'rgba(234,179,8,0.9)' : 'rgba(234,179,8,0.25)' }}
                  data-testid={`moment-dot-${i}`} />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default MemorialMomentsCarousel;
